/**
 * Customer Management API Service
 * Handles all customer-related API calls
 */

import { apiClient } from './client';
import type { Customer, AssignedJob, EmploymentHistory, Education, Certification, Project, BillingTransaction, CustomerSubscription } from '@repo/types';
import { CareerLevel, EducationLevel, WorkAuthorization, AvailabilityStatus, JobSearchStatus, JobType, WorkMode, SkillLevel } from '@repo/types';

export interface PaginatedCustomersResponse {
  data: Customer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateCustomerData {
  // Required fields
  name: string;
  email: string;
  phone: string;
  password: string;
  
  // Optional basic fields — structured address (mirrors the API's
  // `CreateCustomerRequest`, which uses snake_case for these). The API derives
  // a read-only `location` string from these on the way out; it is not part
  // of the create payload.
  country?: string;
  state?: string;
  city?: string;
  pincode?: string;
  street_address?: string;

  // Professional Background
  years_of_experience?: number;
  current_job_title?: string;
  current_company?: string;
  current_industry?: string;
  career_level?: CareerLevel;
  employment_history?: EmploymentHistory[];

  // Job Preferences
  preferred_job_titles?: string[];
  preferred_locations?: string[];
  preferred_categories?: string[];
  preferred_industries?: string[];
  preferred_work_modes?: WorkMode[];
  preferred_job_types?: JobType[];
  relocation_willing?: boolean;
  remote_only?: boolean;
  
  // Compensation & Benefits
  expected_salary_min?: number;
  expected_salary_max?: number;
  salary_currency?: string;
  salary_negotiable?: boolean;
  benefits_preferences?: Record<string, unknown>;
  
  // Education & Skills
  education_history?: Education[];
  highest_education_level?: EducationLevel;
  certifications?: Certification[];
  languages?: Array<{
    language: string;
    proficiency: 'native' | 'fluent' | 'professional' | 'limited';
  }>;
  skills_proficiency?: Record<string, SkillLevel>;
  
  // Work Authorization & Availability
  work_authorization?: WorkAuthorization;
  requires_sponsorship?: boolean;
  notice_period_days?: number;
  available_start_date?: string; // ISO date string
  availability_status?: AvailabilityStatus;
  
  // Portfolio & Online Presence
  portfolio_url?: string;
  linkedin_url?: string;
  github_url?: string;
  personal_website?: string;
  projects?: Project[];

  // Career Goals & Preferences
  career_objectives?: string;
  target_companies?: string[];
  deal_breakers?: string[];
  must_haves?: string[];
  
  // Profile Metadata
  profile_visibility?: string; // 'public' | 'private' | 'recruiters_only'
  job_search_status?: JobSearchStatus;
}

export interface UpdateCustomerData {
  name?: string;
  email?: string;
  phone?: string;
  // Structured address (mirrors the API's `UpdateCustomerRequest`, which uses
  // camelCase here). `location` itself is a derived, read-only field on the
  // API and is intentionally not part of the update payload.
  country?: string;
  state?: string;
  city?: string;
  pincode?: string;
  streetAddress?: string;
}

export interface CustomerApiFilters {
  search?: string;
  subscriptionTier?: number;
  subscriptionStatus?: string;
  careerLevel?: string[];
  jobSearchStatus?: string[];
  workAuthorization?: string[];
  availabilityStatus?: string[];
  location?: string;
  minExperience?: number;
  maxExperience?: number;
}

/**
 * Customer API service class
 */
export class CustomerApiService {
  /**
   * Get all customers with pagination and filters
   */
  async getCustomers(filters: CustomerApiFilters = {}, page = 1, limit = 20) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (filters.search) params.append('search', filters.search);
    if (filters.subscriptionTier) params.append('subscriptionTier', filters.subscriptionTier.toString());
    if (filters.subscriptionStatus) params.append('subscriptionStatus', filters.subscriptionStatus);
    if (filters.location) params.append('location', filters.location);
    if (filters.minExperience !== undefined) params.append('minExperience', filters.minExperience.toString());
    if (filters.maxExperience !== undefined) params.append('maxExperience', filters.maxExperience.toString());

    const response = await apiClient.get<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(`/customers?${params.toString()}`);
    return {
      data: response.data.data.map(customer => this.transformCustomer(customer)),
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      totalPages: response.data.totalPages,
    };
  }

  /**
   * Helper to ensure array fields are actual arrays
   */
  private ensureArray(value: any): any[] | undefined {
    if (!value) return undefined;
    if (Array.isArray(value)) return value;
    // Handle PostgreSQL array format (string representation)
    if (typeof value === 'string') {
      try {
        // Try parsing as JSON first
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : undefined;
      } catch {
        // If not JSON, might be PostgreSQL array format like "{value1,value2}"
        if (value.startsWith('{') && value.endsWith('}')) {
          return value.slice(1, -1).split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        }
      }
    }
    return undefined;
  }

  /**
   * Normalise an ISO/date value to an ISO string (or undefined).
   */
  private toIso(value: any): string | undefined {
    if (!value) return undefined;
    return typeof value === 'string' ? value : new Date(value).toISOString();
  }

  /**
   * Normalise the backend customer payload (camelCase) into the shared
   * `Customer`/`CustomerProfile` shape. The backend already returns camelCase;
   * this mainly coerces PostgreSQL array columns and date values, and passes the
   * nested `jobPreferences` jsonb (preferred_job_roles / preferred_work_type /
   * industry_preferences / expected_salary_range) through untouched.
   */
  private transformCustomer(backendCustomer: any): Customer {
    if (!backendCustomer) return backendCustomer;

    // The API already returns camelCase keys matching the Customer type, so pass
    // everything through and only special-case the fields that need coercion.
    // (An explicit allowlist silently dropped personal details, summary, coding
    // profiles, etc. as the type grew — spreading keeps admin ⇄ API in parity.)
    return {
      ...backendCustomer,

      // Array-ish fields: normalise null/undefined/non-array to [].
      preferredLocations: this.ensureArray(backendCustomer.preferredLocations),
      preferredCategories: this.ensureArray(backendCustomer.preferredCategories),
      preferredJobTypes: this.ensureArray(backendCustomer.preferredJobTypes),
      targetCompanies: this.ensureArray(backendCustomer.targetCompanies),
      dealBreakers: this.ensureArray(backendCustomer.dealBreakers),
      mustHaves: this.ensureArray(backendCustomer.mustHaves),

      // Dates: normalise to ISO strings for consistent formatting.
      availableStartDate: this.toIso(backendCustomer.availableStartDate),
      lastProfileUpdate: this.toIso(backendCustomer.lastProfileUpdate),
      verificationDate: this.toIso(backendCustomer.verificationDate),
      createdAt: this.toIso(backendCustomer.createdAt),
      updatedAt: this.toIso(backendCustomer.updatedAt),

      // Lead counters default to 0.
      leadsConsumed: backendCustomer.leadsConsumed ?? 0,
      leadsRemaining: backendCustomer.leadsRemaining ?? 0,
    };
  }

  /**
   * Get customer by ID
   */
  async getCustomerById(id: string) {
    const response = await apiClient.get<any>(`/customers/${id}`);
    // Response structure: { data: Customer (camelCase), success: boolean, message?: string }
    return this.transformCustomer(response.data);
  }

  /**
   * Create a new customer
   */
  async createCustomer(data: CreateCustomerData) {
    const response = await apiClient.post<any>('/customers', data);
    // Response structure: { data: Customer (camelCase), success: boolean, message?: string }
    return this.transformCustomer(response.data);
  }

  /**
   * Update an existing customer
   */
  async updateCustomer(id: string, data: UpdateCustomerData) {
    const response = await apiClient.put<any>(`/customers/${id}`, data);
    // Response structure: { data: Customer (camelCase), success: boolean, message?: string }
    return this.transformCustomer(response.data);
  }

  /**
   * Delete a customer (soft delete)
   */
  async deleteCustomer(id: string) {
    await apiClient.delete(`/customers/${id}`);
  }

  /**
   * Assign a job to a customer
   */
  async assignJob(customerId: string, jobId: string, reason?: string) {
    await apiClient.post(`/customers/${customerId}/jobs/${jobId}/assign`, {
      reason: reason || undefined,
    });
  }

  /**
   * Get assigned jobs for a customer
   */
  async getAssignedJobs(customerId: string): Promise<AssignedJob[]> {
    const response = await apiClient.get<AssignedJob[]>(`/customers/${customerId}/assigned-jobs`);
    return response.data;
  }

  /**
   * Get a customer's billing/purchase history (staff/admin only).
   */
  async getBillingHistory(customerId: string): Promise<BillingTransaction[]> {
    const response = await apiClient.get<BillingTransaction[]>(`/subscriptions/history/${customerId}`);
    return response.data;
  }

  /**
   * Put a customer on a plan directly — upgrade/downgrade without payment
   * (staff/admin only).
   */
  async changeSubscriptionPlan(customerId: string, planId: string): Promise<CustomerSubscription> {
    const response = await apiClient.put<CustomerSubscription>(
      `/subscriptions/customer/${customerId}/plan`,
      { plan_id: planId }
    );
    return response.data;
  }

  /**
   * Cancel a customer's active subscription (staff/admin only).
   */
  async cancelSubscription(customerId: string): Promise<void> {
    await apiClient.delete(`/subscriptions/customer/${customerId}`);
  }
}

// Export singleton instance
export const customerApi = new CustomerApiService();
